(() => {
  if (window.__elysmCtPdpV2Ready) return;
  window.__elysmCtPdpV2Ready = true;

  const imageCache = new Map();
  let stickyObserver = null;
  let stickyPrimary = null;
  let stickyBars = [];

  const scheduleIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1200 });
    } else {
      window.setTimeout(callback, 120);
    }
  };

  const getImageData = (thumb) => ({
    source: thumb?.dataset.ctSrc || '',
    sourceSet: thumb?.dataset.ctSrcset || '',
    sizes: thumb?.dataset.ctSizes || '(min-width: 750px) 52vw, calc(100vw - 32px)'
  });

  const warmImage = (thumb, priority = 'low') => {
    const { source, sourceSet, sizes } = getImageData(thumb);
    if (!source) return null;

    const key = sourceSet || source;
    const cached = imageCache.get(key);
    if (cached) {
      if (priority === 'high') cached.loader.fetchPriority = 'high';
      return cached;
    }

    const loader = new Image();
    loader.decoding = 'async';
    loader.fetchPriority = priority;
    loader.sizes = sizes;
    if (sourceSet) loader.srcset = sourceSet;
    loader.src = source;

    const ready = new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve(loader.naturalWidth > 0);
      };

      loader.addEventListener('load', finish, { once: true });
      loader.addEventListener('error', finish, { once: true });

      if (loader.decode) {
        loader.decode().then(finish).catch(() => {
          if (loader.complete) finish();
        });
      } else if (loader.complete) {
        finish();
      }
    });

    const record = { loader, ready, source, sourceSet, sizes };
    imageCache.set(key, record);
    return record;
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.ctReady) return;
    gallery.dataset.ctReady = 'true';

    const main = gallery.querySelector('[data-ct-main-image]');
    const stage = gallery.querySelector('.elysm-ct-gallery__stage');
    const counter = gallery.querySelector('[data-ct-count]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-ct-thumb]'));
    const thumbIndexes = new Map(thumbs.map((thumb, index) => [thumb, index]));
    let activeIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true'));
    let activeThumb = thumbs[activeIndex] || null;
    let imageRequest = 0;
    let touchStartX = null;

    const preloadNeighbours = () => {
      if (thumbs.length < 2) return;
      scheduleIdle(() => {
        warmImage(thumbs[(activeIndex + 1) % thumbs.length]);
        if (thumbs.length > 2) warmImage(thumbs[(activeIndex - 1 + thumbs.length) % thumbs.length]);
      });
    };

    const showDetailedImage = (thumb, request) => {
      const record = warmImage(thumb, 'high');
      if (!record) return;

      record.ready.then((loaded) => {
        if (!loaded || request !== imageRequest || thumb !== activeThumb) return;
        main.sizes = record.sizes;
        if (record.sourceSet) main.srcset = record.sourceSet;
        main.src = record.source;
      });
    };

    const scrollThumbAfterPaint = (thumb) => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          thumb.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        });
      });
    };

    const selectImage = (index, options = {}) => {
      if (!main || !thumbs.length || index < 0) return;
      const nextIndex = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[nextIndex];

      if (thumb === activeThumb) {
        if (options.focus) thumb.focus({ preventScroll: true });
        return;
      }

      const thumbImage = thumb.querySelector('img');
      const previewSource = thumbImage?.currentSrc || thumbImage?.src || thumb.dataset.ctSrc;
      const request = ++imageRequest;

      activeThumb?.setAttribute('aria-current', 'false');
      thumb.setAttribute('aria-current', 'true');
      activeThumb = thumb;
      activeIndex = nextIndex;

      main.removeAttribute('srcset');
      if (previewSource) main.src = previewSource;
      main.alt = thumb.dataset.ctAlt || '';
      main.fetchPriority = 'high';
      if (counter) counter.textContent = (nextIndex + 1) + ' / ' + thumbs.length;
      if (options.focus) thumb.focus({ preventScroll: true });

      showDetailedImage(thumb, request);
      if (options.scroll) scrollThumbAfterPaint(thumb);
      preloadNeighbours();
    };

    gallery.addEventListener('click', (event) => {
      const thumb = event.target instanceof Element ? event.target.closest('[data-ct-thumb]') : null;
      if (!thumb || !gallery.contains(thumb)) return;
      selectImage(thumbIndexes.get(thumb));
    });

    gallery.addEventListener('pointerover', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      const thumb = event.target instanceof Element ? event.target.closest('[data-ct-thumb]') : null;
      if (thumb && gallery.contains(thumb)) warmImage(thumb);
    }, { passive: true });

    stage?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      selectImage(activeIndex + (event.key === 'ArrowRight' ? 1 : -1), { focus: true, scroll: true });
    });

    stage?.addEventListener('touchstart', (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    }, { passive: true });

    stage?.addEventListener('touchend', (event) => {
      if (touchStartX === null) return;
      const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
      const distance = touchEndX - touchStartX;
      touchStartX = null;
      if (Math.abs(distance) < 45) return;
      selectImage(activeIndex + (distance < 0 ? 1 : -1), { scroll: true });
    }, { passive: true });

    preloadNeighbours();
  };

  const initTabs = (tabs) => {
    if (tabs.dataset.ctReady) return;
    tabs.dataset.ctReady = 'true';

    const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
    const wrap = tabs.closest('[data-ct-tabs-wrap]');
    const panels = new Map(
      Array.from(wrap?.querySelectorAll('[role="tabpanel"]') || []).map((panel) => [panel.id, panel])
    );
    let activeButton = buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0];

    const selectTab = (button, focus = false) => {
      if (!button || button === activeButton) {
        if (focus) button?.focus();
        return;
      }

      const oldPanel = panels.get(activeButton?.getAttribute('aria-controls'));
      const newPanel = panels.get(button.getAttribute('aria-controls'));

      activeButton?.setAttribute('aria-selected', 'false');
      if (activeButton) activeButton.tabIndex = -1;
      if (oldPanel) oldPanel.hidden = true;

      button.setAttribute('aria-selected', 'true');
      button.tabIndex = 0;
      if (newPanel) newPanel.hidden = false;
      activeButton = button;
      if (focus) button.focus();
    };

    tabs.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
      if (button && tabs.contains(button)) selectTab(button);
    });

    tabs.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const current = buttons.indexOf(document.activeElement);
      if (current < 0) return;
      event.preventDefault();
      let next = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : current + (event.key === 'ArrowRight' ? 1 : -1);
      next = (next + buttons.length) % buttons.length;
      selectTab(buttons[next], true);
    });
  };

  const initSticky = () => {
    stickyBars = Array.from(document.querySelectorAll('[data-ct-sticky]'));
    const primaryButton = document.querySelector('[data-ct-primary-atc]');
    if (!stickyBars.length || !primaryButton) return;

    document.body.classList.add('elysm-ct-pdp-active');
    if (primaryButton === stickyPrimary && stickyObserver) return;

    stickyObserver?.disconnect();
    stickyPrimary = primaryButton;
    stickyObserver = new IntersectionObserver(([entry]) => {
      const hasPassedPrimaryButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      stickyBars.forEach((bar) => bar.classList.toggle('is-visible', hasPassedPrimaryButton));
    }, { threshold: 0 });
    stickyObserver.observe(primaryButton);
  };

  const init = (root = document) => {
    if (document.querySelector('.elysm-ct-product')) document.body.classList.add('elysm-ct-pdp-active');
    root.querySelectorAll('[data-ct-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-ct-tabs]').forEach(initTabs);
    initSticky();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
